import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/shared/ui/PageHeader'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { Card } from '@/shared/ui/Card'
import { Button } from '@/shared/ui/Button'
import { SlideOver } from '@/shared/ui/SlideOver'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { FilterBar } from '@/shared/ui/FilterBar'
import { StatusBadge } from '@/shared/ui/Badge'
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react'
import { formatDate } from '@/shared/utils'
import { useDebounce } from '@/shared/hooks'
import { useCoupons, useCreateUpdateCoupon, useDeleteCoupon } from '@/api/hooks'
import { Can } from '@/shared/ui/Can'

interface Coupon {
  id:number; code?:string; name?:string; discount_value?:number; discount_type?:string
  is_active?:boolean; status?:string; expiry_date?:string; usage_limit?:number; max_uses?:number
}

export function CouponPage() {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const [search, setSearch] = useState('')
  const debounced = useDebounce(search)
  const { data, isLoading, isError } = useCoupons({ search: debounced || undefined })
  const save = useCreateUpdateCoupon()
  const del  = useDeleteCoupon()

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Coupon | null>(null)
  const [form, setForm] = useState({ code:'', discount_value:'', usage_limit:'', expiry_date:'' })
  const [toDelete, setToDelete] = useState<Coupon | null>(null)

  const rows: Coupon[] = data?.data ?? data ?? []

  const openNew  = () => { setEditing(null); setForm({ code:'', discount_value:'', usage_limit:'', expiry_date:'' }); setOpen(true) }
  const openEdit = (c: Coupon) => { setEditing(c); setForm({ code:c.code??'', discount_value:String(c.discount_value??''), usage_limit:String(c.usage_limit??c.max_uses??''), expiry_date:c.expiry_date?.slice(0,10)??'' }); setOpen(true) }

  const handleSave = async () => {
    if (!form.code.trim() || !form.discount_value) return
    const payload: Record<string, unknown> = {
      code: form.code, discount_value: Number(form.discount_value),
      usage_limit: form.usage_limit ? Number(form.usage_limit) : undefined,
      expiry_date: form.expiry_date || undefined,
    }
    if (editing?.id) payload.id = editing.id
    if (editing?.id && !(await confirm({
      title: t('common.confirmSaveTitle'), description: t('common.confirmSaveDescription'),
      confirmLabel: t('common.saveChanges'), variant: 'primary',
    }))) return
    await save.mutateAsync(payload)
    setOpen(false)
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Coupon Management"
        breadcrumbs={[{label:'Dashboard',to:'/dashboard'},{label:'Coupons'}]}
        action={<Can perm="POST /admin/discount/*"><Button icon={<Plus size={14}/>} size="sm" onClick={openNew}>New Coupon</Button></Can>}/>
      <Card padding={false}>
        <div className="p-5 border-b border-gray-100">
          <FilterBar search={{ value:search, onChange:setSearch, placeholder:'Search coupon code…' }}/>
        </div>
        {isError && <p className="px-5 py-4 text-sm text-red-500">Failed to load coupons.</p>}
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24}/></div>
        ) : (
          <div className="overflow-x-auto">
          <table className="drivys-table w-full">
            <thead className="bg-gray-50"><tr><th>Code</th><th>Discount</th><th>Usage Limit</th><th>Expiry</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-gray-400 py-12 text-sm">No coupons found</td></tr>
              ) : rows.map(c => (
                <tr key={c.id}>
                  <td className="font-mono font-semibold text-gray-900">{c.code ?? c.name ?? '—'}</td>
                  <td className="font-bold">{c.discount_value ?? 0}{c.discount_type==='PERCENTAGE'||!c.discount_type?'%':''}</td>
                  <td className="text-gray-700">{c.usage_limit ?? c.max_uses ?? '∞'}</td>
                  <td className="text-gray-500 text-sm">{c.expiry_date ? formatDate(c.expiry_date) : '—'}</td>
                  <td><StatusBadge status={c.status ?? (c.is_active===false?'inactive':'active')}/></td>
                  <td>
                    <div className="flex gap-1">
                      <Can perm="POST /admin/discount/*">
                        <button onClick={()=>openEdit(c)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-50 text-brand-600 hover:bg-brand-100"><Edit size={13}/></button>
                      </Can>
                      <Can perm="DELETE /admin/discount/*">
                        <button onClick={()=>setToDelete(c)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100"><Trash2 size={13}/></button>
                      </Can>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <SlideOver open={open} onClose={()=>setOpen(false)} title={editing ? 'Edit Coupon' : 'New Coupon'}>
        <div className="space-y-4">
          <Fld label="Coupon Code *" value={form.code} onChange={v=>setForm(f=>({...f,code:v.toUpperCase()}))} placeholder="SUMMER25"/>
          <Fld label="Discount Value *" type="number" value={form.discount_value} onChange={v=>setForm(f=>({...f,discount_value:v}))} placeholder="25"/>
          <Fld label="Usage Limit" type="number" value={form.usage_limit} onChange={v=>setForm(f=>({...f,usage_limit:v}))} placeholder="Leave blank for unlimited"/>
          <Fld label="Expiry Date" type="date" value={form.expiry_date} onChange={v=>setForm(f=>({...f,expiry_date:v}))}/>
          <Button className="w-full justify-center" loading={save.isPending} onClick={handleSave}>Save</Button>
        </div>
      </SlideOver>

      <ConfirmDialog open={!!toDelete} onClose={()=>setToDelete(null)}
        onConfirm={async()=>{ if(toDelete) await del.mutateAsync(toDelete.id); setToDelete(null) }}
        title="Delete coupon?" description={`Coupon "${toDelete?.code}" will be permanently deleted.`} loading={del.isPending}/>
    </div>
  )
}

function Fld({ label, value, onChange, placeholder, type='text' }: { label:string; value:string; onChange:(v:string)=>void; placeholder?:string; type?:string }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">{label}</label>
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"/>
    </div>
  )
}
